import { useFilterStore } from '../stores/filterStore.js';
import { dataService } from './dataService.js';
import { getInternalFilterParams } from '../utils/filterMapping.js';
import { appConfig } from '../config/appConfig.js';

/**
 * FilterManager - Orchestrates filter operations between UI, state, and API
 * Provides centralized filter management and coordinates with map viewport changes
 */
export class FilterManager {
  constructor() {
    this.dataService = dataService;
    this.isLoading = false;
    this.lastRequestTime = null;
    this.debounceTimeout = null;
    this.debounceDelay = appConfig.api.rateLimitDelay; // Use configured delay
  }

  /**
   * Apply filters and fetch updated mine data
   * @param {Function} onSuccess - Callback for successful data fetch
   * @param {Function} onError - Callback for error handling
   * @param {boolean} debounce - Whether to debounce the request (default: true)
   */
  async applyFilters(onSuccess, onError, debounce = true) {
    // Clear any existing debounce timeout
    if (this.debounceTimeout) {
      clearTimeout(this.debounceTimeout);
    }

    const executeFilter = async () => {
      try {
        this.isLoading = true;
        this.lastRequestTime = Date.now();

        // Get current filter state and viewport
        const { getAPIParams } = useFilterStore.getState();
        const apiParams = getAPIParams();

        // Validate that we have viewport bounds
        if (!apiParams.bounds) {
          console.warn('FilterManager: No viewport bounds available, skipping filter application');
          return;
        }

        // Fetch filtered mine data using DataService
        const result = await this.dataService.applyFilters(apiParams.filters, apiParams.bounds, apiParams.zoom);

        // DataService.applyFilters returns data directly, not wrapped response
        console.log('FilterManager: Applied filters successfully', {
          filterCount: Object.keys(apiParams.filters).length,
          resultCount: result?.features?.length || 0,
          filters: apiParams.filters
        });

        if (onSuccess) {
          onSuccess(result);
        }
      } catch (error) {
        console.error('FilterManager: Unexpected error during filter application', error);
        this.handleFilterError(error.message || 'Unexpected error', onError);
      } finally {
        this.isLoading = false;
      }
    };

    // Execute immediately or with debounce
    if (debounce) {
      this.debounceTimeout = setTimeout(executeFilter, this.debounceDelay);
    } else {
      await executeFilter();
    }
  }

  /**
   * Sync filters with current map viewport and apply
   * @param {Object} bounds - Map viewport bounds {north, south, east, west}
   * @param {number} zoom - Map zoom level
   * @param {Function} onSuccess - Success callback
   * @param {Function} onError - Error callback
   */
  async syncWithViewport(bounds, zoom, onSuccess, onError) {
    try {
      // Update viewport in filter store
      const { setViewport } = useFilterStore.getState();
      setViewport(bounds, zoom);

      console.log('FilterManager: Viewport updated', { bounds, zoom });

      // Apply filters with new viewport
      await this.applyFilters(onSuccess, onError, false); // No debounce for viewport changes
    } catch (error) {
      console.error('FilterManager: Error syncing with viewport', error);
      this.handleFilterError(error.message || 'Viewport sync error', onError);
    }
  }

  /**
   * Validate current filter state
   * @returns {Object} Validation result with isValid and errors
   */
  validateFilters() {
    const { getFilters } = useFilterStore.getState();
    const filters = getFilters();
    const errors = [];

    // Validate mineral type
    if (!['all', 'critical', 'non-critical'].includes(filters.mineralType)) {
      errors.push('Invalid mineral type selected');
    }

    // Validate arrays are actually arrays
    const arrayFields = getInternalFilterParams().filter(param => param !== 'mineralType');
    arrayFields.forEach(field => {
      if (!Array.isArray(filters[field])) {
        errors.push(`${field} must be an array`);
      }
    });

    // Validate that at least one mine status is selected
    if (filters.mineStatus.length === 0) {
      errors.push('At least one mine status must be selected');
    }

    return {
      isValid: errors.length === 0,
      errors
    };
  }

  /**
   * Handle filter errors with graceful degradation
   * @param {string} error - Error message
   * @param {Function} onError - Error callback
   */
  handleFilterError(error, onError) {
    console.error('FilterManager: Handling filter error', error);
    
    // Provide default error handling if no callback provided
    if (onError) {
      onError(error);
    } else {
      // Fallback: Show user-friendly message
      console.warn('FilterManager: No error handler provided, using default behavior');
      // Could integrate with existing UI notification system here
    }

    // Reset loading state
    this.isLoading = false;
  }

  /**
   * Get current loading state
   * @returns {boolean} Whether filters are currently being applied
   */
  getLoadingState() {
    return this.isLoading;
  }

  /**
   * Cancel any pending filter operations
   */
  cancelPendingOperations() {
    if (this.debounceTimeout) {
      clearTimeout(this.debounceTimeout);
      this.debounceTimeout = null;
    }
    this.isLoading = false;
  }

  /**
   * Reset all filters to default state and apply
   * @param {Function} onSuccess - Success callback
   * @param {Function} onError - Error callback
   */
  async resetAllFilters(onSuccess, onError) {
    try {
      const { resetFilters } = useFilterStore.getState();
      resetFilters();
      
      console.log('FilterManager: Filters reset to default state');
      
      // Apply reset filters
      await this.applyFilters(onSuccess, onError, false);
    } catch (error) {
      console.error('FilterManager: Error resetting filters', error);
      this.handleFilterError(error.message || 'Reset error', onError);
    }
  }
}

// Export singleton instance
export const filterManagerInstance = new FilterManager();